"use client";
import React, { useState, useMemo, useCallback } from "react";
import { Container, Modal } from "react-bootstrap";
import Image from "next/image";
import dynamic from "next/dynamic";
const CountUp = dynamic(() => import("react-countup"), { ssr: false });
import SectionTitle from "@/components/common-components/SectionTitle";
import CustomButton from "@/components/common-components/CustomButton";
import { Cancel01Icon } from "@/utlility/icons";

import "./index.css";

// ─────────────────────────────────────────────────────────────────────────────
// Utility helpers
// ─────────────────────────────────────────────────────────────────────────────

const getYouTubeEmbedLink = (url) => {
  if (!url) return "";

  try {
    const { hostname, pathname, searchParams } = new URL(url);

    // Short-link format: youtu.be/<videoId>
    if (hostname === "youtu.be") {
      return `https://www.youtube.com/embed/${pathname.slice(1)}?autoplay=1`;
    }

    if (hostname.includes("youtube.com")) {
      // Already an embed URL — return unchanged
      if (pathname.includes("/embed/")) return url;

      // Standard watch URL: ?v=<videoId>
      const videoId = searchParams.get("v");
      if (videoId) return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
  } catch {
    // Malformed URL – fall through to return the original value
  }

  return url;
};

// ─────────────────────────────────────────────────────────────────────────────
// VideoPopup component
// ─────────────────────────────────────────────────────────────────────────────


const VideoPopup = ({
  show,
  onHide,
  iframeSrc,
  iframeTitle = "YouTube video player",
  iframeHeight = "450",
  iframeClassName = "w-100 rounded-4",
  size = "lg",
}) => {
  // Memoised so re-renders caused by parent state don't recompute on every tick
  const embedSrc = useMemo(() => getYouTubeEmbedLink(iframeSrc), [iframeSrc]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size={size}
      centered
      aria-labelledby="video-modal-title"
      dialogClassName="bg-transparent"
      contentClassName="bg-transparent border-0"
      className="video-popup-modal p-0 border-0"
    >
      <Modal.Body className="p-0 border-0 bg-transparent text-end">
        {/* Close button */}
        <button
          onClick={onHide}
          aria-label="Close video"
          className="btn p-0 mb-3 border-0 bg-transparent text-white"
          style={{ cursor: "pointer", transition: "transform 0.2s" }}
          onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.2)")}
          onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          <Cancel01Icon width={24} height={24} color="#ffffff" strokeWidth="2.5" />
        </button>

        {/* Mount iframe only when visible to avoid background audio playback */}
        {show && (
          <iframe
            className={iframeClassName}
            height={iframeHeight}
            src={embedSrc}
            title={iframeTitle}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const VideoThumbnail = ({ src, alt, title, onClick }) => (
  <div className="video-thumbnail-wrapper">
    <Image
      src={src}
      width={620}
      height={520}
      className="about-main-image"
      alt={alt}
      title={title}
      onClick={onClick}
      priority
      fetchPriority="high"
      sizes="(max-width: 768px) 100vw, 620px"
    />

    {/* Accessible play button overlay */}
    <div
      className="play-button-overlay"
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label="Play presentation video"
      onKeyDown={(e) => e.key === "Enter" && onClick()}
    >
      <Image
        src="/images/youtube-icon.webp"
        className="play-icon"
        alt="Play video"
        width={80}
        height={80}
      />
    </div>
  </div>
);

const StatItem = ({ header, label }) => {
  // Extract leading digits; remainder ("+", "k", "%", " yrs", …) becomes the suffix
  const numericValue = parseInt(header, 10) || 0;
  const suffix = header.replace(/[0-9]/g, "");

  return (
    <div className="sub-section me-4">
      <div className="d-flex align-items-center">
        <h4 className="fw-semibold number mb-0">
          <CountUp start={0} end={numericValue} delay={0} duration={4} enableScrollSpy scrollSpyDelay={100}>
            {({ countUpRef }) => <span ref={countUpRef} />}
          </CountUp>
          <span className="text-uppercase fw-medium extention">{suffix}</span>
        </h4>
      </div>
      <p className="fw-medium mb-0">{label}</p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const AboutUsSection = ({ content = [], subSections = [], isHome = false }) => {
  // ── Parse CMS content by type ──────────────────────────────────────────────
  const findContent = (type) => content.find((item) => item.content_type === type);

  const headerData = findContent("header");
  const imageData = findContent("image");
  const descriptionData = findContent("html_text");
  const linkData = findContent("link");

  const sectionHeading = headerData?.content ?? "About Our Dental Practice";

  // ── Video modal state ──────────────────────────────────────────────────────
  const [showVideo, setShowVideo] = useState(false);

  // Stable callback reference; prevents unnecessary re-renders in child props
  const openVideo = useCallback(() => setShowVideo(true), []);
  const closeVideo = useCallback(() => setShowVideo(false), []);

  return (
    <section className={`about-us-section${isHome ? " mb-0" : ""}`} id="about-us">
      <Container>
        <div className="about-container">

          {/* ── Visuals column ── */}
          <div className="about-visuals" data-aos="fade-right">
            {imageData?.content && (
              <VideoThumbnail
                src={imageData.content}
                alt={`Experience our dental services – ${sectionHeading}`}
                title={sectionHeading}
                onClick={openVideo}
              />
            )}
          </div>

          {/* ── Info column ── */}
          <div className="about-info" data-aos="fade-left">
            <div className="about-heading">
              <SectionTitle
                sectionName="About Us"
                title={sectionHeading}
                isH1={isHome}
              />
            </div>

            {/* Rich-text body from CMS */}
            {descriptionData?.content && (
              <div
                className="html-content"
                dangerouslySetInnerHTML={{ __html: descriptionData.content }}
              />
            )}

            {/* Home page: "Discover More" CTA */}
            {isHome && linkData?.content && (
              <div className="about-action">
                <CustomButton
                  text="Discover More"
                  srText={`Read more about ${sectionHeading}`}
                  href="/about-us"
                  className="premium-btn"
                />
              </div>
            )}

            {/* About Us page: animated stat counters */}
            {!isHome && subSections.length > 0 && (
              <div className="exp-section d-flex mt-4">
                {subSections.map((subSection, i) => {
                  const sectionContents = subSection?.section_contents ?? [];
                  const titleItem = sectionContents.find((c) => c?.content_type === "header");
                  const textItem = sectionContents.find((c) => c?.content_type === "text");

                  // Skip incomplete sub-sections silently
                  if (!titleItem || !textItem) return null;

                  return (
                    <StatItem
                      key={i}
                      header={titleItem.content}
                      label={textItem.content}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Container>

      {/* ── YouTube video modal ── */}
      <VideoPopup
        show={showVideo}
        onHide={closeVideo}
        iframeSrc={linkData?.content}
        iframeTitle={`${sectionHeading} presentation video`}
      />
    </section>
  );
};

export default AboutUsSection;